import { useEffect, useRef, useState } from 'react';

const faqs = [
  {
    q: '體驗課需要費用嗎？',
    a: '體驗課以半價優惠提供。第一次可以來教室坐坐，親手感受古箏的聲音和觸感，聊聊你的狀態和期待，不需要任何準備，也沒有報名壓力。',
  },
  {
    q: '一週要上幾次課？有固定時數要求嗎？',
    a: '完全沒有強制要求。大多數學員每週上一次課，每次 60 分鐘。你可以根據自己的生活節奏調整，某些時期比較忙可以間隔拉長，這裡的課程設計本來就是配合你的生活，而不是要你配合它。',
  },
  {
    q: '沒有古箏，也可以開始學嗎？',
    a: '可以。初學階段可以先使用教室的練習琴，不需要在開始之前就購買樂器。學了幾堂課、確認自己真的想繼續之後，再依老師建議選購適合的古箏，這樣的決定會更不容易後悔。',
  },
  {
    q: '多久可以彈出一首完整的曲子？',
    a: '每個人的節奏都不一樣，無法給出統一的答案。一般來說，3–6 個月的基礎學習後，許多學員可以彈出較簡單的旋律片段。這裡不以「學完幾首曲子」衡量進度，而更在意你在學習過程中是否感到安心、是否有真正的感受。',
  },
  {
    q: '中間有事情可以暫停嗎？',
    a: '當然可以。生活有變化是很正常的事，課程可以依需求暫停或調整節奏。希望你在需要的時候能隨時暫停，也能在準備好的時候輕鬆回來，不需要有任何愧疚感。',
  },
  {
    q: '實體課教室在哪裡？方便停車嗎？',
    a: '教室位於台中市豐原區豐原大道一段 269-1 號，近豐原火車站步行約 12 分鐘。教室附近有路邊停車格，機車可停放附近。如有交通上的疑問，加入 LINE@ 詢問即可。',
  },
  {
    q: '孩子和成人的課程一樣嗎？',
    a: '不完全一樣。孩子和成人在學習動力、注意力持續時間和理解方式上都有差異，所以課程內容和互動方式會依學員年齡做調整。不過基本精神是一樣的：不強迫、不比較、根據每個人的狀態陪伴前進。',
  },
];

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function CoursesFaq() {
  const { ref, visible } = useVisible();
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIdx(openIdx === i ? null : i);

  return (
    <section id="courses-faq" className="py-20 md:py-28 bg-[#f2ede5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px bg-[#b08060]"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
                  FAQ
                </span>
              </div>
              <h2
                className="font-serif-tc text-[#2c2420] leading-[1.3]"
                style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
              >
                課程常見問題
              </h2>
            </div>
            <p className="text-[0.9375rem] text-[#7a6e62] max-w-sm">
              還有其他問題嗎？加入 LINE@ 直接問，沒有笨問題。
            </p>
          </div>

          {/* FAQ Accordion — 2 columns on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-[#ede5d8] overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-[#faf8f5] transition-colors"
                >
                  <span className="text-[0.9375rem] font-medium text-[#2c2420] leading-[1.5]">
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                    <i
                      className={`text-[#9a8470] transition-transform duration-200 ${
                        openIdx === i ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIdx === i ? 'max-h-60' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 pb-5 text-[0.9375rem] leading-[1.75] text-[#5a4e42]">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-10 bg-white rounded-2xl border border-[#ede5d8] p-6">
            <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-[#f5f0e8]">
              <i className="ri-chat-smile-3-line text-[#7a6248] text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="text-[0.9375rem] font-semibold text-[#2c2420] mb-0.5">還有其他問題？</p>
              <p className="text-[0.875rem] text-[#7a6e62]">直接加入 LINE@ 問我，什麼都可以問，不會被推銷。</p>
            </div>
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#06C755] hover:bg-[#05a848] text-white text-[0.875rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-line-fill"></i>
              加入 LINE@ 諮詢
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
